import { useEffect, useState, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi } from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { count, exact } from '../lib/format.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import { RecordScreen } from '../components/Record.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  HoldButton,
  Identity,
  Note,
  OutcomeNote,
  Panel,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { EditCredential, canEditCredential } from '../modals/EditCredential.js';
import { RotateCredential, canRotateCredential } from '../modals/RotateCredential.js';
import { agentOf, roleOf, serviceOf } from './Developer.js';

/**
 * One service credential, at `#/developer/:id` (Jon, 2026-10-23).
 *
 * The list row could only say "active" and offer three buttons. After the auth
 * service started keeping a rotated key's old secret working for twelve hours
 * (RidgeWill/auth#649), a key could have several secrets that work at once, and
 * the question "which ones, and until when" had nowhere to be answered. So the
 * credential became a record like an agent or an application, and its actions
 * moved here with it. Revoke stays on the list as well, because during an
 * incident the list is where somebody already is.
 */
export function CredentialRecord({ id }: { id: string }) {
  const { state, reload } = useAsync(() => adminApi.apiKey(id), [id]);
  const action = useAction();
  const mayChange = useCan('credential:revoke');
  const [editing, setEditing] = useState(false);
  const [rotating, setRotating] = useState(false);

  /**
   * A previous secret falls off when its window closes, without a reload. The
   * server only returns open windows, so asking it again at the earliest
   * deadline is what removes the row; it is the same answer a fresh load gives.
   */
  const nextDeadline =
    state.status === 'ready'
      ? Math.min(...state.data.previousSecrets.map((p) => new Date(p.validUntil).getTime()))
      : Infinity;
  useEffect(() => {
    if (!Number.isFinite(nextDeadline)) return;
    const timer = setTimeout(reload, Math.max(0, nextDeadline - Date.now()) + 1000);
    return () => clearTimeout(timer);
  }, [nextDeadline, reload]);

  return (
    <AsyncBoundary state={state}>
      {(k) => {
        const revoked = k.enabled === false;
        return (
          <RecordScreen
            back={{ to: '/developer', label: 'Credentials' }}
            title={k.name ?? 'Unnamed credential'}
            identifier={`${k.start ?? k.prefix ?? '—'}…`}
            badges={
              <span style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap' }}>
                {revoked ? <Badge tone="danger">revoked</Badge> : <Badge tone="good">active</Badge>}
                {agentOf(k.metadata) && <Badge tone="warn">agent</Badge>}
                {roleOf(k.metadata) && <Badge tone="warn">{roleOf(k.metadata)}</Badge>}
              </span>
            }
            actions={
              revoked ? null : (
                <span style={{ display: 'inline-flex', gap: '.4rem' }}>
                  {canEditCredential(k) && (
                    <Button
                      disabled={action.running || !mayChange}
                      title={mayChange ? undefined : whyDisabled('editing credentials')}
                      onClick={() => setEditing(true)}
                    >
                      Edit
                    </Button>
                  )}
                  {canRotateCredential(k) && (
                    <Button
                      disabled={action.running || !mayChange}
                      title={mayChange ? 'A new secret for this key. The current one keeps working for 12 hours.' : whyDisabled('rotating credentials')}
                      onClick={() => setRotating(true)}
                    >
                      Rotate
                    </Button>
                  )}
                  <HoldButton
                    disabled={action.running || !mayChange}
                    title={
                      mayChange
                        ? `Hold for a second and a half to revoke ${k.name ?? 'this key'}. Every secret it has stops working at once.`
                        : whyDisabled('revoking credentials')
                    }
                    onHold={() =>
                      void action
                        .run(async () => {
                          const r = await adminApi.revokeApiKey(k.id);
                          return r.alreadyRevoked
                            ? 'Already revoked. Nothing changed.'
                            : `Revoked ${k.name ?? 'the key'}. Every secret it had stopped working.`;
                        })
                        .then(() => reload())
                    }
                  >
                    Revoke
                  </HoldButton>
                </span>
              )
            }
          >
            <OutcomeNote outcome={action.outcome} />

            <Panel title="Details">
              <div style={{ ...facts, padding: '1rem 1.2rem' }}>
                <Fact label="Application">
                  <span style={{ fontFamily: 'ui-monospace, monospace' }}>{serviceOf(k.metadata)}</span>
                </Fact>
                <Fact label="Owner">
                  {k.owner ? <Identity email={k.owner.email} /> : <Badge tone="danger">owner missing</Badge>}
                </Fact>
                <Fact label="Last used">
                  <When iso={k.lastRequest} />
                  {k.requestCount ? ` · ${count(k.requestCount)} requests` : null}
                </Fact>
                <Fact label="Expires">
                  {k.expiresAt ? <When iso={k.expiresAt} /> : <span style={{ color: C.muted }}>never</span>}
                </Fact>
                <Fact label="Issued">
                  <When iso={k.createdAt} />
                </Fact>
                <Fact label="Permissions">
                  <span style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap' }}>
                    {k.grants?.length ? k.grants.map((g) => <Badge key={g}>{g}</Badge>) : <span style={{ color: C.muted }}>none</span>}
                    {k.unmanagedPermissions ? <Badge tone="warn">other permissions</Badge> : null}
                  </span>
                </Fact>
              </div>
            </Panel>

            <Panel title="Secrets">
              {revoked ? (
                <Note>This credential is revoked, so none of its secrets work.</Note>
              ) : (
                <>
                  <Table columns={['Secret', 'Status', 'Replaced', 'Works until']}>
                    <tr>
                      <Td mono>{k.start ?? k.prefix ?? '—'}…</Td>
                      <Td>
                        <Badge tone="good">current</Badge>
                      </Td>
                      <Td>—</Td>
                      <Td>
                        {k.expiresAt ? <span title={exact(k.expiresAt)}>{exact(k.expiresAt)}</span> : <span style={{ color: C.muted }}>until rotated or revoked</span>}
                      </Td>
                    </tr>
                    {k.previousSecrets.map((p) => (
                      <tr key={p.replacedAt}>
                        <Td mono>{p.start ? `${p.start}…` : <span style={{ color: C.muted }}>earlier secret</span>}</Td>
                        <Td>
                          <Badge tone="warn">previous</Badge>
                        </Td>
                        <Td>
                          <When iso={p.replacedAt} />
                        </Td>
                        <Td>{exact(p.validUntil)}</Td>
                      </tr>
                    ))}
                  </Table>
                  <Note>
                    {k.previousSecrets.length
                      ? 'A previous secret keeps working for 12 hours after the rotation that replaced it, then drops off this list. Revoke the credential to stop every secret at once.'
                      : 'Only the current secret works. Rotating keeps the current one working for 12 hours, so there is time to update everything that uses it.'}
                  </Note>
                </>
              )}
            </Panel>

            {editing && (
              <EditCredential
                apiKey={k}
                onClose={() => setEditing(false)}
                onSaved={reload}
              />
            )}
            {rotating && (
              <RotateCredential
                apiKey={k}
                onClose={() => {
                  setRotating(false);
                  reload();
                }}
              />
            )}
          </RecordScreen>
        );
      }}
    </AsyncBoundary>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: '.2rem' }}>
      <span style={factLabel}>{label}</span>
      <span style={{ fontSize: '.88rem' }}>{children}</span>
    </div>
  );
}

const facts = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  gap: '.9rem 1.4rem',
};
const factLabel = {
  fontSize: '.72rem',
  letterSpacing: '.06em',
  textTransform: 'uppercase' as const,
  color: C.muted,
  fontWeight: 640,
};
